// src/pages/partner/institution/Page_Partner_Institution.js
import React, { useState, useEffect } from "react";
import { Tab, Button, Message, Loader } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { BasicModal } from "../../../../components/Shared";
import { useAuth } from "../../../../hooks";
import { InstitutionsTable } from "./InstitutionsTable";
import { InstitutionForm } from "../../../../components/Admin/InstitutionCmp/InstitutionForm";
import { PlanesAPI, InstitutionApi } from "../../../../api";
import "./Page_Partner_Institution.scss";

const planesApi = new PlanesAPI();
const institutionApi = new InstitutionApi();

export function Page_Partner_Institution() {
  const { t } = useTranslation("institution");
  const { accessToken, user } = useAuth();

  // 🗂️ Estados generales
  const [showModal, setShowModal] = useState(false);
  const [noPlansError, setNoPlansError] = useState(false);
  const [reload, setReload] = useState(false);
  const [editingInst, setEditingInst] = useState(null);

  // 📦 Planes
  const [plans, setPlans] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // 🏢 Instituciones
  const [institutions, setInstitutions] = useState([]);
  const [loadingInst, setLoadingInst] = useState(true);

  // 📑 Filtro de pestaña: 0=Activas, 1=Inactivas
  const [activeTab, setActiveTab] = useState(0);
  const activeFilter = activeTab === 0;

  // 1️⃣ Cargar planes al montar
  useEffect(() => {
    (async () => {
      setLoadingPlans(true);
      try {
        const resp = await planesApi.getPlans(accessToken, {
          page: 1,
          limit: 50,
        });
        setPlans(resp.plans || []);
      } catch (err) {
        console.error("Error cargando planes", err);
      } finally {
        setLoadingPlans(false);
      }
    })();
  }, [accessToken]);

  // 2️⃣ Cargar instituciones cada que cambie pestaña o reload
  useEffect(() => {
    (async () => {
      setLoadingInst(true);
      try {
        const res = await institutionApi.getInstitutions(
          accessToken,
          user.user._id,
          activeFilter, // true=activos, false=inactivos
        );
        setInstitutions(res.institutions || []);
      } catch (err) {
        console.error("Error cargando instituciones", err);
        setInstitutions([]);
      } finally {
        setLoadingInst(false);
      }
    })();
  }, [accessToken, user.user._id, activeFilter, reload]);

  const onReload = () => setReload((r) => !r);

  // 3️⃣ Alta de institución (revisa licencias)
  const handleNew = () => {
    const availIds = (user.user.planEducaActivations || [])
      .filter((a) => a.quantity > 0)
      .map((a) => a.plan);
    const filtered = plans.filter((p) => availIds.includes(p._id));
    if (!filtered.length) {
      setNoPlansError(true);
      return;
    }
    setAvailablePlans(filtered);
    setEditingInst(null);
    setNoPlansError(false);
    setShowModal(true);
  };

  // 4️⃣ Editar
  const handleEdit = (inst) => {
    setEditingInst(inst);
    setAvailablePlans([inst.planEduca]);
    setShowModal(true);
  };

  // 5️⃣ Borrar
  const handleDelete = async (id) => {
    try {
      await institutionApi.deleteInstitution(accessToken, id);
      onReload();
    } catch (err) {
      console.error("Error borrando institución", err);
    }
  };

  // 6️⃣ Toggle activo/inactivo
  const handleToggle = async (id) => {
    try {
      // invoca toggle sin saber valor, el backend lo alterna
      await institutionApi.toggleInstitutionActivation(accessToken, id);
      onReload();
    } catch (err) {
      console.error("Error toggling institución", err);
    }
  };

  // Pestañas: Activas / Inactivas
  const panes = [
    {
      menuItem: t("partner.institutions.tabActive"),
      render: () => (
        <Tab.Pane attached={false}>
          {loadingInst ? (
            <Loader active inline="centered" />
          ) : institutions.length === 0 ? (
            <Message info content={t("partner.institutions.emptyActive")} />
          ) : (
            <InstitutionsTable
              data={institutions}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: t("partner.institutions.tabInactive"),
      render: () => (
        <Tab.Pane attached={false}>
          {loadingInst ? (
            <Loader active inline="centered" />
          ) : institutions.length === 0 ? (
            <Message info content={t("partner.institutions.emptyInactive")} />
          ) : (
            <InstitutionsTable
              data={institutions}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          )}
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className="page-partner-institution">
      <div className="header">
        {noPlansError && (
          <Message
            error
            header={t("partner.institutions.noPlansTitle")}
            content={t("partner.institutions.noPlansBody")}
          />
        )}
        <Button primary onClick={handleNew}>
          {t("partner.institutions.new")}
        </Button>
      </div>

      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        activeIndex={activeTab}
        onTabChange={(_, data) => setActiveTab(data.activeIndex)}
      />

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={
          editingInst
            ? t("partner.institutions.modalEdit")
            : t("partner.institutions.modalNew")
        }
        size="large"
      >
        {loadingPlans ? (
          <Loader active inline="centered" />
        ) : (
          <InstitutionForm
            user={user}
            onReload={onReload}
            close={() => setShowModal(false)}
            plans={availablePlans}
            institution={editingInst}
          />
        )}
      </BasicModal>
    </div>
  );
}
